import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Save, Plus, Trash2, ArrowRightCircle, Info } from "lucide-react";
import api from "../../lib/api";

export default function Redirects() {
  const [defaults, setDefaults] = useState({});
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/redirects");
    setDefaults(data.defaults || {});
    setRows(Object.entries(data.overrides || {}).map(([from, to]) => ({ _key: `${from}|${to}`, from, to })));
  };
  useEffect(() => { load(); }, []);

  const rowId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `r_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const addRow = () => setRows([...rows, { _key: rowId(), from: "/", to: "/" }]);
  const update = (i, k, v) => { const arr = [...rows]; arr[i] = { ...arr[i], [k]: v }; setRows(arr); };
  const remove = (i) => { const arr = [...rows]; arr.splice(i, 1); setRows(arr); };

  const save = async () => {
    setSaving(true);
    try {
      const map = {};
      rows.forEach((r) => {
        const f = (r.from || "").trim();
        const t = (r.to || "").trim();
        if (f.startsWith("/") && t.startsWith("/") && f !== t) map[f] = t;
      });
      await api.put("/admin/redirects", { redirects: map });
      toast.success("Redirects saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-redirects-page">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">keep google happy</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">URL redirects</h1>
          <p className="text-sm text-[#6E6074] mt-1 max-w-2xl">Old Wix URLs that Google or your customers might still visit automatically redirect to the new site. Edit the list below to add any extra legacy paths.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={addRow} className="sac-btn-secondary !py-2 !px-4 text-sm" data-testid="redirects-add"><Plus size={14} /> Add row</button>
          <button onClick={save} disabled={saving} className="sac-btn-primary disabled:opacity-60" data-testid="redirects-save"><Save size={16} /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      <div className="admin-surface p-5 mb-5 border-l-4 border-[#8EC9C7]">
        <div className="flex items-start gap-3 text-sm text-[#5D2C8F]">
          <Info size={16} className="mt-0.5 shrink-0" />
          <div>
            <p><strong>Built-in defaults</strong> below are always active — covering the URLs Wix usually creates (<code className="bg-[#F4EDDD] px-1 rounded">/about-1</code>, <code className="bg-[#F4EDDD] px-1 rounded">/services</code>, etc). Your overrides above take precedence.</p>
            <p className="mt-1 text-[#6E6074]">For bigger domain moves, we recommend also setting HTTP-level 301s at your edge (Cloudflare / deployment platform) for the best SEO weight transfer.</p>
          </div>
        </div>
      </div>

      <div className="admin-surface p-5 mb-6">
        <h3 className="font-display text-lg text-[#5D2C8F] mb-3">Your overrides</h3>
        {rows.length === 0 && <div className="text-sm text-[#6E6074] italic mb-2">No custom redirects yet. Click "Add row" to create one.</div>}
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={r._key || `row-${i}`} className="flex items-center gap-2" data-testid={`redirect-row-${i}`}>
              <input value={r.from} onChange={(e) => update(i, "from", e.target.value)} placeholder="/old-path" className="flex-1 bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm font-mono" data-testid={`redirect-from-${i}`} />
              <ArrowRightCircle size={16} className="text-[#A685BD] shrink-0" />
              <input value={r.to} onChange={(e) => update(i, "to", e.target.value)} placeholder="/new-path" className="flex-1 bg-white border border-[#E5E0D8] rounded-lg px-3 py-2 text-sm font-mono" data-testid={`redirect-to-${i}`} />
              <button onClick={() => remove(i)} className="text-red-500 hover:bg-red-50 rounded p-2" data-testid={`redirect-remove-${i}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-surface p-5">
        <h3 className="font-display text-lg text-[#5D2C8F] mb-3">Built-in defaults ({Object.keys(defaults).length})</h3>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm font-mono text-[#5D2C8F]">
          {Object.entries(defaults).map(([from, to]) => (
            <div key={from} className="flex items-center gap-2 py-1">
              <span className="text-[#6E6074]">{from}</span>
              <ArrowRightCircle size={13} className="text-[#A685BD]" />
              <span>{to}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
